import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Profile from "./profile/profile";
import Message from "../sidebar/messagesAdmin/messages";
import Notifications from "./notifications/notification";
import useAdminUnreadMessages from "./messagesAdmin/Useadminunreadmessages"; 
import { PopupContext } from "../../PopupContext";
import { ArrowLeft } from "lucide-react";

/* ============================================================
   Compteur de notifications admin
   ⚠️ Ces listes doivent rester IDENTIQUES à celles de Notifications.js
   ============================================================ */
const ADMIN_ROLES = ["admin", "commercial_admin"];
const PAYMENT_TYPES = [
  "PAYMENT_MADE",
  "PAYMENT_ACCEPTED",
  "PAYMENT_REJECTED",
  "INVOICE_RECEIPT_SUBMITTED",
];
const WITHDRAWAL_TYPES = ["WITHDRAWAL_MADE", "WITHDRAW_ACCEPTED", "WITHDRAW_REJECTED"];
const PROFILE_TYPES = ["PROFIL_CREATED", "SUBSCRIPTION_RENEWAL_REQUESTED"];

const countUnread = (notifs = []) =>
  notifs.filter((n) => {
    if (n.read || !ADMIN_ROLES.includes(n.recipientRole)) return false;
    return (
      n.type === "USER_CREATED" ||
      n.type === "QUIZ_PASSED" ||
      PAYMENT_TYPES.includes(n.type) ||
      WITHDRAWAL_TYPES.includes(n.type) ||
      PROFILE_TYPES.includes(n.type)
    );
  }).length;

const SidebarAdmin = () => {
  const navigate = useNavigate();
  const [isProfilePopupVisible, setProfilePopupVisible] = useState(false);
  const [isMessagesPopupVisible, setMessagesPopupVisible] = useState(false);
  const [isNotificationPopupVisible, setNotificationPopupVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  /* Messages clients non lus (badge de l'icône Messages) */
  const [unreadMessages, setUnreadMessages, refreshUnreadMessages] =
    useAdminUnreadMessages(15000);

  const API_URL = process.env.REACT_APP_API_URL;
  const { openPackParent, popupRef, closeAllPopups } = useContext(PopupContext);
  const logoRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    navigate("/login");
  }, [navigate]);

  /* ---------- Compteur notifications : rafraîchi toutes les 15 s ---------- */
  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(countUnread(res.data));
    } catch (error) {
      console.error("Error fetching admin notifications:", error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    }
  }, [API_URL, handleLogout]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  /* ---------- Popups ---------- */
  const toggleProfilePopup = () => {
    closeAllPopups();
    setProfilePopupVisible(!isProfilePopupVisible);
  };

  const toggleMessagesPopup = () => {
    closeAllPopups();
    // à la fermeture on resynchronise le badge avec le serveur
    if (isMessagesPopupVisible) refreshUnreadMessages();
    setMessagesPopupVisible(!isMessagesPopupVisible);
  };

  const toggleNotificationPopup = () => {
    closeAllPopups();
    if (isNotificationPopupVisible) fetchUnreadCount();
    setNotificationPopupVisible(!isNotificationPopupVisible);
  };

  /* ---------- Rendu d'un popup ---------- */
  const renderPopup = (onToggle, content) => (
    <div className="sidebar-with-popup">
      <div className="popup-header">
        <ArrowLeft
          className="sidebar-back-arrow"
          size={24}
          color="#fff"
          onClick={onToggle}
        />
      </div>
      <div className="overlayUB" onClick={() => openPackParent(null)}>
        <div
          ref={popupRef}
          className="PopupUnlimitedBonus-content"
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="sidebarclient">
        <div className="sidebar-overlayclient"></div>
        <div className="logos-container-navbarclient">
          <div className="logo-itemclient" onClick={toggleProfilePopup}>
            <img
              src="/images/sidebar/profile.PNG"
              alt="Profile"
              className="logoclient"
              ref={logoRef}
            />
          </div>

          {/* Messages + badge des messages non lus */}
          <div className="logo-itemclient" onClick={toggleMessagesPopup}>
            <img
              src="/images/sidebar/message.PNG"
              alt="Messages"
              className="logoclient"
            />
            {unreadMessages > 0 && (
              <span className="notification-badgeclient">
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </span>
            )}
          </div>

          <div className="logo-itemclient" onClick={toggleNotificationPopup}>
            <img
              src="/images/sidebar/notifications.PNG"
              alt="Notifications"
              className="logoclient"
            />
            {unreadCount > 0 && (
              <span className="notification-badgeclient">{unreadCount}</span>
            )}
          </div>
          <div className="logo-itemclient">
            <img
              src="/images/sidebar/favorite.PNG"
              alt="Favorites"
              className="logoclient"
            />
          </div>
          <div className="logo-itemclient">
            <img
              src="/images/sidebar/settings.PNG"
              alt="Settings"
              className="logoclient"
            />
          </div>
          <div className="logo-itemclient" onClick={handleLogout}>
            <img
              src="/images/sidebar/logout.PNG"
              alt="Logout"
              className="logoclient"
            />
          </div>
        </div>
      </div>

      {createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            overflow: "visible",
            zIndex: 1000,
          }}
        >
          {isProfilePopupVisible &&
            renderPopup(
              toggleProfilePopup,
              <Profile onClose={toggleProfilePopup} />
            )}

          {isMessagesPopupVisible &&
            renderPopup(
              toggleMessagesPopup,
              /* onUnreadChange : le popup envoie le total réel à la sidebar */
              <Message
                onClose={toggleMessagesPopup}
                onUnreadChange={setUnreadMessages}
              />
            )}

          {isNotificationPopupVisible &&
            renderPopup(
              toggleNotificationPopup,
              <Notifications
                onClose={toggleNotificationPopup}
                onUnreadChange={setUnreadCount}
              />
            )}
        </div>,
        document.body
      )}
    </>
  );
};

export default SidebarAdmin;